/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { useSettingsContext } from 'src/components/settings';
import TrialBanner from 'src/components/trial-banner/TrialBanner';
import UpgradePromptDialog from 'src/sections/pricing/UpgradePromptDialog';
//
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import HelpDialog from './HelpDialog';

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const settings = useSettingsContext();
  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean();

  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const isHorizontal = settings.themeLayout === 'horizontal';
  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;
  const renderHorizontal = <NavHorizontal />;
  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  const renderNav = lgUp && isMini ? renderNavMini : renderNavVertical;

  const renderFloatingHelpButton = (
    <>
      <Tooltip title="Help & Support" placement="left">
        <IconButton
          onClick={handleOpenHelp}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1100,
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            border: '1.5px solid',
            borderColor: '#0062FF',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: 'grey.50',
              borderColor: '#0052D9',
              transform: 'scale(1.06) translateY(-2px)',
            }
          }}
        >
          <Box
            component="img"
            src="/assets/MeruDeskShort.png"
            alt="Help & Support"
            sx={{ width: 22, height: 22 }}
          />
        </IconButton>
      </Tooltip>

      <HelpDialog open={openHelp} onClose={handleCloseHelp} />
    </>
  );

  let layoutContent = null;

  if (isHorizontal) {
    layoutContent = (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>
          <TrialBanner />
          {children}
        </Main>
      </>
    );
  } else if (isMini) {
    layoutContent = (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>
            <TrialBanner />
            {children}
          </Main>
        </Box>
      </>
    );
  } else {
    layoutContent = (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        {/* 1. Header stays at the top */}
        <Header onOpenNav={nav.onTrue} />

        {/* 2. Content area starts AFTER header height */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            minHeight: 0,
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {renderNav}

          <Main>
            <TrialBanner />
            {children}
          </Main>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100dvh', overflow: 'hidden' }}>
      {layoutContent}
      {renderFloatingHelpButton}
      <UpgradePromptDialog />
    </Box>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};